import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve(true);
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const PLAN_NAMES = { team: "Team", starter: "Starter", pro: "Pro" };
const PENDING_SUB_KEY = "nimbus_pending_subscription";
const JUST_STARTED_MS = 15 * 60 * 1000;
const POLL_CAP = 3;
let activeWatch = null;

function readPending() {
  try {
    const raw = sessionStorage.getItem(PENDING_SUB_KEY);
    if (!raw) return null;
    const pending = JSON.parse(raw);
    if (!pending || typeof pending !== "object" || !pending.id) return null;
    return pending;
  } catch {
    return null;
  }
}

function writePending(pending) {
  try { sessionStorage.setItem(PENDING_SUB_KEY, JSON.stringify(pending)); } catch { /* storage blocked */ }
}

export function rememberPendingSubscription(id) {
  if (!id) return;
  writePending({ id: String(id), startedAt: Date.now(), polls: 0 });
}

export function pendingSubscriptionId() {
  const pending = readPending();
  return pending ? String(pending.id) : "";
}

function clearPendingSubscription() {
  try { sessionStorage.removeItem(PENDING_SUB_KEY); } catch { /* storage blocked */ }
}

/* A checkout this tab just opened, still inside the small poll budget. Later visits do not start another burst. */
function justStarted(pending) {
  return !!(pending && pending.id && typeof pending.startedAt === "number"
    && Date.now() - pending.startedAt < JUST_STARTED_MS && (pending.polls || 0) < POLL_CAP);
}

function takePoll() {
  const pending = readPending();
  if (!justStarted(pending)) return null;
  pending.polls = (pending.polls || 0) + 1;
  writePending(pending);
  return pending;
}

/* Ask the server to fetch this checkout from Razorpay and apply it if it has been paid. */
export async function refreshSubscriptionStatus(id) {
  const subId = id || pendingSubscriptionId();
  if (!subId) return null;
  try {
    const res = await axios.get(`${API}/subscriptions/${encodeURIComponent(subId)}/status`, { withCredentials: true });
    const status = res.data?.status || res.data?.subscription?.status;
    if (status && status !== "created") clearPendingSubscription();
    return res.data;
  } catch (e) {
    if (e?.response?.status === 404) clearPendingSubscription();
    throw e;
  }
}

/* After the user opens checkout: a few status checks with backoff, not another burst on every page load. */
export function watchSubscriptionStatus({ onActive } = {}) {
  if (!justStarted(readPending())) return () => {};
  if (activeWatch) activeWatch.stop();
  let timer = null;
  let stopped = false;
  const watch = {
    stop() {
      stopped = true;
      if (timer) clearTimeout(timer);
      if (activeWatch === watch) activeWatch = null;
    },
  };
  activeWatch = watch;
  const tick = async () => {
    if (stopped) return;
    const pending = takePoll();
    if (!pending) return;
    try {
      const data = await refreshSubscriptionStatus(pending.id);
      const status = data?.status || data?.subscription?.status;
      if (status && status !== "created") {
        if (onActive) onActive(data);
        return;
      }
    } catch (e) {
      if (e?.response?.status === 404 || e?.response?.status === 401) return;
    }
    if (stopped || !justStarted(readPending())) return;
    const polls = readPending()?.polls || 1;
    timer = setTimeout(tick, 2000 * (2 ** (polls - 1)));
  };
  timer = setTimeout(tick, 2000);
  return () => watch.stop();
}

/* India: Team, Starter or Pro as a Razorpay subscription in ₹ (UPI Autopay or card mandate), renewing monthly
   until cancelled in Settings. The amount comes from the server's plan, the same number /api/pricing shows. */
export async function subscribeTeamInr({ user, onDone }) {
  return subscribeInr({ plan: "team", user, onDone });
}

export async function subscribeInr({ plan, user, onDone }) {
  const r = await axios.post(`${API}/subscriptions/${plan}`);
  rememberPendingSubscription(r.data.subscription && r.data.subscription.id);
  const ok = await loadRazorpay();
  if (!ok || !window.Razorpay) {
    if (r.data.short_url) { window.location.href = r.data.short_url; return; }
    throw new Error("Couldn't load Razorpay checkout.");
  }
  const rzp = new window.Razorpay({
    key: r.data.key_id,
    subscription_id: r.data.subscription_id,
    name: "Nimbus",
    description: `${PLAN_NAMES[plan] || "Nimbus"} · ₹${Number(r.data.amount_inr).toLocaleString("en-IN")} a month, inclusive of GST, renews monthly`,
    prefill: { email: user?.email || "", name: user?.name || "" },
    handler: () => {
      refreshSubscriptionStatus().catch(() => {});
      watchSubscriptionStatus();
      if (onDone) onDone();
    },
  });
  rzp.open();
}

/* Outside India: a US$ card payment through Stripe for a 30-day pass that doesn't renew. */
export async function buyTeamPassUsd() {
  const r = await axios.post(`${API}/payments/checkout`, { package_id: "team" });
  if (!r.data.checkout_url) throw new Error("No checkout URL");
  window.location.href = r.data.checkout_url;
}
